import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import Popup from 'reactjs-popup'
import { useMutation, useQueryClient } from 'react-query'
import { ResourcesOverviewTagShape } from '../../api/api_data_types'
import { createResourcesOverviewTag, deleteResourcesOverviewTag, updateResourcesOverviewTag } from '../../api/resources_overview_tags'
import ErrorMessage from '../job_plannings/list_app/ErrorMessage'

type TagPopupProps = {
  open: boolean
  mode: 'create' | 'edit'
  tag?: ResourcesOverviewTagShape | null
  onClose: () => void
}

const HEX_COLOR_FORMAT = /^#[0-9A-F]{6}$/i
const DEFAULT_TAG_COLOR = '#4C7D75'

export default function TagPopup({ open, mode, tag, onClose }: TagPopupProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [color, setColor] = useState(DEFAULT_TAG_COLOR)

  const onMutationSuccess = () => {
    queryClient.invalidateQueries(['resources_overview_tags'])
    onClose()
  }

  const createMutation = useMutation(createResourcesOverviewTag, { onSuccess: onMutationSuccess })
  const updateMutation = useMutation(({ id, payload }: { id: number; payload: { name: string; color: string } }) => updateResourcesOverviewTag(id, payload), {
    onSuccess: onMutationSuccess,
  })
  const deleteMutation = useMutation((id: number) => deleteResourcesOverviewTag(id), { onSuccess: onMutationSuccess })

  useEffect(() => {
    if (mode === 'edit' && tag) {
      setName(tag.name || '')
      setColor((tag.color || DEFAULT_TAG_COLOR).toUpperCase())
      return
    }

    setName('')
    setColor(DEFAULT_TAG_COLOR)
  }, [mode, tag, open])

  const error = createMutation.error || updateMutation.error || deleteMutation.error
  const isLoading = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading
  const canSubmit = Boolean(name.trim() && HEX_COLOR_FORMAT.test(color))

  const title = useMemo(() => {
    if (mode === 'edit') return 'Rediger tag'
    return 'Tilføj tag'
  }, [mode])

  const closePopup = () => {
    createMutation.reset()
    updateMutation.reset()
    deleteMutation.reset()
    onClose()
  }

  const onSave = () => {
    if (!canSubmit) return

    const payload = {
      name: name.trim(),
      color: color.toUpperCase(),
    }

    if (mode === 'create') {
      createMutation.mutate(payload)
      return
    }

    if (!tag) return
    updateMutation.mutate({ id: tag.id, payload })
  }

  const onDelete = () => {
    if (!tag) return
    if (!window.confirm('Er du sikker på at du vil slette tagget?')) return

    deleteMutation.mutate(tag.id)
  }

  return (
    <Popup
      open={open}
      onClose={closePopup}
      position="center center"
      contentStyle={{ width: 'auto', maxWidth: '100%', minWidth: '420px', zIndex: 9999 }}
      overlayStyle={{ zIndex: 9998 }}
      modal
      nested
      closeOnDocumentClick={false}
    >
      <div className="modal-popup">
        <a className="close" onClick={closePopup}>
          &times;
        </a>
        <div className="popup-header">
          <h6 className="modal-title">{title}</h6>
        </div>
        <div className="popup-content">
          <div className="form-group">
            <label htmlFor="resources-overview-tag-name">Navn</label>
            <input
              id="resources-overview-tag-name"
              type="text"
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="resources-overview-tag-color">Farve</label>
            <input
              id="resources-overview-tag-color"
              type="color"
              className="form-control"
              value={color}
              onChange={e => setColor(e.target.value.toUpperCase())}
              disabled={isLoading}
            />
          </div>
          <ErrorMessage isError={Boolean(error)} error={error} className="error-message mb-2" />
        </div>
        <div className="popup-actions d-flex justify-content-between">
          <div>
            {mode === 'edit' && (
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={onDelete} disabled={isLoading}>
                Slet
              </button>
            )}
          </div>
          <div>
            <button type="button" className="btn btn-outline-secondary btn-sm mr-2" onClick={closePopup} disabled={isLoading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={!canSubmit || isLoading}>
              Save
            </button>
          </div>
        </div>
      </div>
    </Popup>
  )
}
